import { LAWYER_TYPE_LAWYER } from '@/shared/constants/lawyerTypeConstants';
import {
  ORGANIZATION_TYPE_CLUB,
  ORGANIZATION_TYPE_BUREAU,
} from '@/shared/constants/organizationTypeConstants';
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { declination } from '@/shared/utils/declination';
import { buildCityDropdownItems } from '@/shared/utils/buildCityDropdownItems';
import type { CityDropdownSource } from '@/shared/utils/buildCityDropdownItems';

import type { ILawyerDetail } from '@/entities/lawyer/model/types/lawyer';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';
import type { LawyerMeta } from '@/pages/lawyer/model/types/useLawyerDetail';

export const getLawyerType = (lawyer: ILawyerDetail): string => {
  const company = lawyer.company;
  if (company && lawyer.role === LAWYER_TYPE_LAWYER) return 'Юридическая компания';
  if (company && company.organisation_type === ORGANIZATION_TYPE_CLUB) {
    return 'Адвокатская коллегия';
  }
  if (company && company.organisation_type === ORGANIZATION_TYPE_BUREAU) {
    return 'Адвокатское бюро';
  }
  if (!company && lawyer.role === LAWYER_TYPE_LAWYER) return 'Юрист';
  return 'Адвокат';
};

export const getFullName = (lawyer: ILawyerDetail): string =>
  `${lawyer.surname ?? ''} ${lawyer.name ?? ''} ${lawyer.middlename ?? ''}`.trim();

export const buildLawyerMeta = (lawyer: ILawyerDetail): LawyerMeta => {
  const type = getLawyerType(lawyer);
  const company = lawyer.company;
  const year = lawyer.activity_date ? new Date(lawyer.activity_date).getFullYear() : 0;

  let name = '';
  let experience = '';

  if (company) {
    experience = `год основания: ${year}`;
    name = company.name ?? '';
  } else {
    const exp = new Date().getFullYear() - year;
    experience = `Стаж: ${exp}${declination(exp, [' год', ' года', ' лет'])}`;
    name = getFullName(lawyer);
  }

  const city = lawyer.addresses?.length ? (lawyer.addresses[0]?.city?.name ?? '') : '';
  const count = lawyer.feedbackCount ?? 0;
  const feedbacks = `${count} ${declination(count, [' отзыв', ' отзыва', ' отзывов'])}`;
  const categories = (lawyer.mainCategory ?? []).map((el) => el.name).join(', ');

  return {
    meta_title: `${type} ${name}, г.${city}, ${experience}, ${feedbacks} - Юрик.ру`,
    meta_description: `${type} ${name}, г.${city} ⚖️ | Специализация: ${categories}, ${feedbacks} - Юрик.ру`,
  };
};

export const buildLawyerBreadcrumbs = (
  lawyer: ILawyerDetail,
  cities: CityDropdownSource[] = [],
): Breadcrumb[] => {
  const address = lawyer.addresses?.length ? lawyer.addresses[0] : null;
  const cityName = address?.city?.name ?? '';
  const citySlug = address?.city?.slug ?? '';
  const name = lawyer.company_id ? (lawyer.company?.name ?? '') : getFullName(lawyer);

  return [
    { id: 1, text: 'Юристы и адвокаты', to: ROOT_CATALOG_URL },
    {
      id: 2,
      text: cityName,
      to: `${ROOT_CATALOG_URL}geo/${citySlug}/`,
      dropdownTitle: 'Города',
      dropdownItems: buildCityDropdownItems(cities, citySlug),
    },
    { id: 3, text: name },
  ];
};
